// src/api/features/suppliers/api.ts
import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  SupplierResult,
  SupplierWithRelations,
  CreateSupplierBody,
  UpdateSupplierBody,
  ListSuppliersQuery,
  PaginatedSuppliers,
} from "./types";

/* =========================
 * Helpers
 * ========================= */

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }

  const s = u.toString();
  return s ? `?${s}` : "";
}

/* =========================
 * URLs (constants)
 * ========================= */

const SUPPLIERS_BASE = PATHS.suppliers.base;

/* =========================
 * Endpoints — CORE SUPPLIERS
 * ========================= */

/** 🔹 Create supplier (and owner user) — DEEP payload */
export async function createSupplier(
  body: CreateSupplierBody
): Promise<{ supplier: SupplierResult; ownerUserId: number }> {
  const dto: any = { ...body };

  // Light normalization / trimming for main strings
  if (dto.supplierCompanyName != null)
    dto.supplierCompanyName = String(dto.supplierCompanyName).trim();
  if (dto.companyName != null)
    dto.companyName = String(dto.companyName).trim();
  if (dto.managerFirstName != null)
    dto.managerFirstName = String(dto.managerFirstName).trim();
  if (dto.managerLastName != null)
    dto.managerLastName = String(dto.managerLastName).trim();
  if (dto.ownerFirstName != null)
    dto.ownerFirstName = String(dto.ownerFirstName).trim();
  if (dto.ownerLastName != null)
    dto.ownerLastName = String(dto.ownerLastName).trim();
  if (dto.ownerEmail != null) dto.ownerEmail = String(dto.ownerEmail).trim();

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[createSupplier] payload →", dto);
  }

  const res = await request<
    { supplier: SupplierResult; ownerUserId: number } |
      ApiEnvelope<{ supplier: SupplierResult; ownerUserId: number }>
  >({
    method: "POST",
    url: SUPPLIERS_BASE,
    data: dto,
  });

  return unwrap<{ supplier: SupplierResult; ownerUserId: number }>(res);
}

/** 🔹 List suppliers (paginated, base fields) */
export async function listSuppliers(
  query: ListSuppliersQuery = {}
): Promise<PaginatedSuppliers> {
  const safePage = Math.max(1, Number(query.page ?? 1));
  const rawLimit = Number(query.limit ?? 10);
  const safeLimit = Math.max(
    1,
    Math.min(100, Number.isFinite(rawLimit) ? rawLimit : 10)
  );

  const url = `${SUPPLIERS_BASE}${qs({
    page: safePage,
    limit: safeLimit,
    q: query.q,
    userId:
      typeof query.userId === "number" && Number.isFinite(query.userId)
        ? query.userId
        : undefined,
    productId:
      typeof query.productId === "number" && Number.isFinite(query.productId)
        ? query.productId
        : undefined,
    country: query.country,
    city: query.city,
    status: query.status,
    includeDeleted: query.includeDeleted ? "true" : undefined,
  })}`;

  const res = await request<
    PaginatedSuppliers | (ApiEnvelope<SupplierResult[]> & { meta?: any })
  >({
    method: "GET",
    url,
  });

  // If server returns { ok, data, meta }
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<SupplierResult[]> & { meta?: any };

    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? safePage),
      pageSize: Number(meta.pageSize ?? meta.limit ?? safeLimit),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  // Otherwise assume backend already returns PaginatedSuppliers
  return res as PaginatedSuppliers;
}

/** 🔹 Get supplier by id — DEEP (with products + contacts) */
export async function getSupplier(
  id: number | string
): Promise<SupplierWithRelations> {
  const res = await request<
    SupplierWithRelations | ApiEnvelope<SupplierWithRelations>
  >({
    method: "GET",
    url: `${SUPPLIERS_BASE}/${id}`, // deep endpoint
  });

  return unwrap<SupplierWithRelations>(res);
}

/** 🔹 Update supplier — DEEP update (base + nested) */
export async function updateSupplier(
  id: number | string,
  body: UpdateSupplierBody
): Promise<SupplierResult> {
  const dto: any = { ...body };

  if (dto.supplierCompanyName != null)
    dto.supplierCompanyName = String(dto.supplierCompanyName).trim();
  if (dto.companyName != null)
    dto.companyName = String(dto.companyName).trim();
  if (dto.managerFirstName != null)
    dto.managerFirstName = String(dto.managerFirstName).trim();
  if (dto.managerLastName != null)
    dto.managerLastName = String(dto.managerLastName).trim();
  if (dto.ownerFirstName != null)
    dto.ownerFirstName = String(dto.ownerFirstName).trim();
  if (dto.ownerLastName != null)
    dto.ownerLastName = String(dto.ownerLastName).trim();
  if (dto.ownerEmail != null) dto.ownerEmail = String(dto.ownerEmail).trim();

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[updateSupplier] id=", id, "payload →", dto);
  }

  const res = await request<SupplierResult | ApiEnvelope<SupplierResult>>({
    method: "PUT",
    url: `${SUPPLIERS_BASE}/${id}`,
    data: dto,
  });

  return unwrap<SupplierResult>(res);
}

/** 🔹 Soft delete supplier */
export async function deleteSupplier(
  id: number | string
): Promise<{ message: string }> {
  const res = await request<
    { message: string } | ApiEnvelope<{ message: string }>
  >({
    method: "DELETE",
    url: `${SUPPLIERS_BASE}/${id}`,
  });

  return unwrap<{ message: string }>(res);
}

/** 🔹 Approve pending supplier registration */
export async function approveSupplier(
  id: number | string
): Promise<SupplierResult> {
  const res = await request<SupplierResult | ApiEnvelope<SupplierResult>>({
    method: "POST",
    url: `${SUPPLIERS_BASE}/${id}/approve`,
  });
  return unwrap<SupplierResult>(res);
}

/** 🔹 Reject pending supplier registration */
export async function rejectSupplier(
  id: number | string
): Promise<SupplierResult> {
  const res = await request<SupplierResult | ApiEnvelope<SupplierResult>>({
    method: "POST",
    url: `${SUPPLIERS_BASE}/${id}/reject`,
  });
  return unwrap<SupplierResult>(res);
}

/** 🔹 Disable login user for approved supplier */
export async function disableSupplierUser(
  id: number | string
): Promise<SupplierResult> {
  const res = await request<SupplierResult | ApiEnvelope<SupplierResult>>({
    method: "POST",
    url: `${SUPPLIERS_BASE}/${id}/disable-user`,
  });
  return unwrap<SupplierResult>(res);
}
